const activeNotices = new WeakMap();
let noticeAudioContext;

function unlockNoticeSound() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return Promise.resolve(false);
  try {
    noticeAudioContext ||= new AudioContextClass();
    if (noticeAudioContext.state === 'running') return Promise.resolve(true);
    const resumed = noticeAudioContext.resume();
    // Starting a source inside the gesture also unlocks Web Audio on iPhone.
    const silent = noticeAudioContext.createOscillator();
    const mute = noticeAudioContext.createGain();
    mute.gain.value = 0;
    silent.connect(mute);
    mute.connect(noticeAudioContext.destination);
    silent.start();
    silent.stop(noticeAudioContext.currentTime + 0.01);
    return Promise.resolve(resumed).then(() => noticeAudioContext.state === 'running').catch(() => false);
  } catch (_) {
    return Promise.resolve(false);
  }
}

function playNoticeSound(volume, fromGesture = false) {
  if (!noticeAudioContext || (!fromGesture && noticeAudioContext.state !== 'running')) return false;
  const now = noticeAudioContext.currentTime;
  const oscillator = noticeAudioContext.createOscillator();
  const gain = noticeAudioContext.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(784, now);
  oscillator.frequency.exponentialRampToValueAtTime(1047, now + 0.23);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.22 * volume, now + 0.035);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.36);
  oscillator.connect(gain);
  gain.connect(noticeAudioContext.destination);
  oscillator.start(now);
  oscillator.stop(now + 0.37);
  return true;
}

function initFloatingNotice(notice) {
  if (activeNotices.has(notice)) return;

  const closeButton = notice.querySelector('[data-iw-floating-notice-close]');
  const soundButton = notice.querySelector('[data-iw-floating-notice-sound]');
  const allMessages = [...notice.querySelectorAll('[data-iw-message]')];
  const messages = allMessages.filter(message => message.textContent.trim());
  const designMode = notice.dataset.designMode === 'true';
  const soundEnabled = notice.dataset.soundEnabled === 'true';
  const soundVolume = Math.min(1, Math.max(0.1, (Number(notice.dataset.soundVolume) || 50) / 100));
  const durationSeconds = Math.min(10, Math.max(2, Number(notice.dataset.durationSeconds) || 5));
  const shownCounts = new Map();
  let nextIndex = 0;
  let waitTimer;
  let hideTimer;
  let soundPlayedForCurrent = false;

  const canShow = message => {
    const limit = Math.min(10, Math.max(1, Number(message.dataset.showLimit) || 1));
    return (shownCounts.get(message) || 0) < limit;
  };
  const nextMessage = () => {
    for (let offset = 0; offset < messages.length; offset++) {
      const index = (nextIndex + offset) % messages.length;
      if (canShow(messages[index])) {
        nextIndex = index;
        return messages[index];
      }
    }
    return null;
  };

  const clearTimers = () => {
    clearTimeout(waitTimer);
    clearTimeout(hideTimer);
  };
  const display = message => {
    allMessages.forEach(item => { item.hidden = item !== message; });
    notice.hidden = !message;
  };
  const schedule = () => {
    if (!messages.length || designMode || document.hidden) return;
    clearTimeout(waitTimer);
    const upcoming = nextMessage();
    if (!upcoming) return;
    const delay = Math.min(60, Math.max(3, Number(upcoming.dataset.delaySeconds) || 15));
    waitTimer = window.setTimeout(show, delay * 1000);
  };
  const dismiss = () => {
    clearTimeout(hideTimer);
    display(null);
    soundPlayedForCurrent = false;
    nextIndex = (nextIndex + 1) % messages.length;
    schedule();
  };
  const show = () => {
    if (!notice.isConnected) { activeNotices.get(notice)?.(); return; }
    if (document.hidden) return;
    const message = nextMessage();
    if (!message) return;
    display(message);
    shownCounts.set(message, (shownCounts.get(message) || 0) + 1);
    soundPlayedForCurrent = soundEnabled && playNoticeSound(soundVolume);
    if (soundEnabled && !soundPlayedForCurrent) {
      unlockNoticeSound().then(ready => {
        if (ready && !notice.hidden && !soundPlayedForCurrent) {
          soundPlayedForCurrent = playNoticeSound(soundVolume);
        }
      });
    }
    hideTimer = window.setTimeout(dismiss, durationSeconds * 1000);
  };
  const handleClose = () => {
    if (designMode) display(null);
    else dismiss();
  };
  const handleVisibility = () => {
    const wasShowing = !notice.hidden;
    clearTimers();
    display(null);
    soundPlayedForCurrent = false;
    if (wasShowing && messages.length) nextIndex = (nextIndex + 1) % messages.length;
    if (!document.hidden) schedule();
  };
  const handleBlockSelect = event => {
    const blockId = event.detail?.blockId;
    const selected = allMessages.find(message => message.dataset.blockId === blockId);
    if (selected) display(selected);
  };
  const handleGesture = event => {
    if (event.target.closest?.('[data-iw-floating-notice-sound]')) return;
    unlockNoticeSound().then(ready => {
      if (!ready) return;
      if (!notice.hidden && !soundPlayedForCurrent) soundPlayedForCurrent = playNoticeSound(soundVolume);
      document.removeEventListener('pointerdown', handleGesture);
      document.removeEventListener('keydown', handleGesture);
      document.removeEventListener('touchstart', handleGesture);
    });
  };
  const handleSoundClick = () => {
    unlockNoticeSound().then(ready => {
      if (!ready) return;
      soundPlayedForCurrent = playNoticeSound(soundVolume);
    });
  };

  closeButton?.addEventListener('click', handleClose);
  soundButton?.addEventListener('click', handleSoundClick);
  if (designMode) {
    display(messages[0] || allMessages[0]);
    document.addEventListener('shopify:block:select', handleBlockSelect);
  } else if (messages.length) {
    document.addEventListener('visibilitychange', handleVisibility);
    if (soundEnabled) {
      document.addEventListener('pointerdown', handleGesture);
      document.addEventListener('keydown', handleGesture);
      document.addEventListener('touchstart', handleGesture, { passive: true });
    }
    display(null);
    schedule();
  } else {
    display(null);
  }

  activeNotices.set(notice, () => {
    clearTimers();
    closeButton?.removeEventListener('click', handleClose);
    soundButton?.removeEventListener('click', handleSoundClick);
    document.removeEventListener('visibilitychange', handleVisibility);
    document.removeEventListener('shopify:block:select', handleBlockSelect);
    document.removeEventListener('pointerdown', handleGesture);
    document.removeEventListener('keydown', handleGesture);
    document.removeEventListener('touchstart', handleGesture);
    activeNotices.delete(notice);
  });
}

document.querySelectorAll('[data-iw-floating-notice]').forEach(initFloatingNotice);
document.addEventListener('shopify:section:load', event => {
  event.target.querySelectorAll('[data-iw-floating-notice]').forEach(initFloatingNotice);
});
document.addEventListener('shopify:section:unload', event => {
  event.target.querySelectorAll('[data-iw-floating-notice]').forEach(notice => activeNotices.get(notice)?.());
});
